import type { SnowmentUnit } from '@snowit/utils/snowmentv2';

import snowment from '@snowit/utils/snowmentv2';

import localforage from 'localforage';

type GetOptions = {
  excludeVersion?: boolean;
  excludeDate?: boolean;
};

type SetOptions = {
  expiration?: number;
  expirationUnits?: SnowmentUnit;
  forever?: boolean;
};

export type StorageEntry<T extends unknown = unknown> = {
  data: T;
  expiration: string;
  version: string;
  forever?: boolean;
};

type IterateFunc<T extends unknown> = (value: T, key: string, iteration?: number) => void;

const VERSION = process.env.SNOWIT_VERSION_HASHED || '1.0.0';

localforage.config({
  driver: localforage.INDEXEDDB,
  version: 1.0,
  name: 'snowit_web',
  storeName: 'snowit_web_storage',
  description: 'Snowit Web',
});

const isExpired = (entry: StorageEntry, opt: GetOptions = {}): boolean => {
  if (entry.forever) return false;
  const { expiration, version } = entry;
  const { excludeDate, excludeVersion } = opt;
  const expVersion = !excludeVersion && version !== VERSION;
  const expDate = !excludeDate && snowment().isAfter(expiration, 'day');
  return expVersion || expDate;
};

export const remove = async (name: string) => {
  try {
    await localforage.removeItem(name);
    return true;
  } catch (error) {
    console.error(error);
    return false;
  }
};

export const get = async <T extends any>(
  name: string,
  fallback?: T,
  opts: GetOptions = {},
): Promise<T | undefined> => {
  try {
    const entry: StorageEntry | null = await localforage.getItem<StorageEntry>(name);
    if (!entry) return fallback;
    const { data } = entry;
    if (isExpired(entry, opts)) {
      await remove(name);
      return fallback;
    }
    return data as T;
  } catch (error) {
    console.error(error);
    throw error;
  }
};

export const set = async <T extends any>(
  name: string,
  value: T,
  opt: SetOptions = {},
): Promise<T> => {
  try {
    const storage: StorageEntry = {
      data: value,
      forever: opt.forever,
      version: VERSION,
      expiration: snowment()
        .add(opt.expiration ?? 10, opt.expirationUnits ?? 'd')
        .libFormat('YYYY-MM-DD HH:mm:ss'),
    };
    await localforage.setItem<StorageEntry>(name, storage);
    return value;
  } catch (error) {
    console.error(error);
    throw error;
  }
};

export const setArray = async <T extends any>(
  name: string,
  value: T,
  opt: SetOptions = {},
): Promise<T[]> => {
  try {
    const current = await get<T[]>(name);
    const data: T[] = [...(current ?? []), value];
    const storage: StorageEntry = {
      data,
      forever: opt.forever,
      version: VERSION,
      expiration: snowment()
        .add(opt.expiration ?? 10, opt.expirationUnits ?? 'd')
        .libFormat('YYYY-MM-DD HH:mm:ss'),
    };
    await localforage.setItem<StorageEntry>(name, storage);
    return data;
  } catch (error) {
    console.error(error);
    throw error;
  }
};

export const clear = async (callback?: Function) => {
  try {
    await localforage.clear();
    if (typeof callback === 'function') return callback(null, true);
    return true;
  } catch (error) {
    console.error(error);
    if (typeof callback === 'function') return callback(error, false);
    return false;
  }
};

export const clearStales = async () => {
  try {
    const keys = await localforage.keys();
    for (const key of keys) {
      await get(key);
    }
  } catch (e) {
    console.error(e);
  }
};

export const iterate = async <T extends unknown>(func: IterateFunc<StorageEntry<T>>) => {
  try {
    localforage.iterate(func);
  } catch (e) {
    console.error(e);
  }
};
